import { useState } from 'react';

import { MenuIcon } from '@/components/MenuIcon';
import { Modal } from '@/components/Modal';
import type { WidgetSlot } from '@/pages/home/useHomeWidgets';
import { widgetByKey, type WidgetSize } from '@/pages/home/widgets/registry';

/**
 * 위젯 관리 — 켜고 끄기 · 순서 · 크기 · 되돌리기. front 의 같은 이름 모달과 같은 모양이다.
 *
 * <p>편집은 <b>이 모달 안에서만</b> 일어나고 저장을 눌러야 반영된다. <b>버튼은 제목 줄에 둔다.</b>
 *
 * <p>드래그를 쓰지 않고 <b>위·아래 버튼</b>으로 옮긴다. 이 앱은 현장에서 손가락으로 쓰는 화면이고,
 * 드래그는 스크롤과 부딪혀 「잡으려다 화면이 움직이는」 일이 잦다 — front 와 갈리는 유일한 자리다.
 *
 * <p>고정 위젯(`fixed`)은 끄지도 옮기지도 못한다. 늘 첫 칸이다.
 */
export function WidgetManagerModal({
  slots,
  customized,
  onSave,
  onReset,
  onClose,
}: {
  slots: WidgetSlot[];
  customized: boolean;
  onSave: (next: WidgetSlot[]) => void;
  onReset: () => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<WidgetSlot[]>(slots);

  /**
   * 켜고 끈다. <b>켤 때는 크기를 기본값으로 되돌린다</b> — 꺼져 있던 위젯에 저장된 크기는
   * 사용자가 고른 값이 아니라 그 위젯이 목록에 처음 붙던 날의 기본값이다.
   */
  const setVisibility = (widgetKey: string, next: boolean) => {
    const defaultSize = widgetByKey(widgetKey)?.defaultSize;
    setDraft((prev) =>
      prev.map((slot) =>
        slot.widgetKey === widgetKey
          ? { ...slot, visible: next, size: next && defaultSize ? defaultSize : slot.size }
          : slot,
      ),
    );
  };

  const setSize = (widgetKey: string, size: WidgetSize) => {
    setDraft((prev) => prev.map((slot) => (slot.widgetKey === widgetKey ? { ...slot, size } : slot)));
  };

  /** 한 칸 위/아래로. 고정 위젯 무리는 넘지 않는다 — 끝에서 더 밀면 아무 일도 하지 않는다. */
  const move = (widgetKey: string, direction: -1 | 1) => {
    setDraft((prev) => {
      const index = prev.findIndex((slot) => slot.widgetKey === widgetKey);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= prev.length) return prev;
      const other = prev[target];
      if (other === undefined || pinned(other.widgetKey) !== pinned(widgetKey)) return prev;

      const next = [...prev];
      const [item] = next.splice(index, 1);
      if (item === undefined) return prev;
      next.splice(target, 0, item);
      return next;
    });
  };

  return (
    <Modal
      title="위젯 관리"
      onClose={onClose}
      actions={
        <>
          {customized && (
            <button
              type="button"
              className="btn-secondary px-3 py-1.5 text-ui-xs"
              title="위젯 구성을 처음 상태로 되돌립니다"
              onClick={() => {
                onReset();
                onClose();
              }}
            >
              되돌리기
            </button>
          )}
          <button type="button" className="btn-secondary px-3 py-1.5 text-ui-xs" onClick={onClose}>
            취소
          </button>
          <button
            type="button"
            className="btn-primary px-3 py-1.5 text-ui-xs"
            onClick={() => {
              onSave(draft);
              onClose();
            }}
          >
            저장
          </button>
        </>
      }
    >
      <ul className="flex flex-col gap-1">
        {draft.map((slot, index) => {
          const definition = widgetByKey(slot.widgetKey);
          if (!definition) return null;
          const fixed = definition.fixed;

          return (
            <li
              key={slot.widgetKey}
              className="flex items-center gap-2 rounded-md px-2 py-2"
              style={{ border: '1px solid var(--border)', opacity: slot.visible ? 1 : 0.45 }}
            >
              <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5">
                {fixed ? (
                  <span className="badge badge-gray shrink-0">고정</span>
                ) : (
                  <input
                    type="checkbox"
                    checked={slot.visible}
                    onChange={(event) => setVisibility(slot.widgetKey, event.target.checked)}
                    aria-label={definition.title}
                  />
                )}
                <span className="shrink-0" style={{ color: 'var(--accent)' }}>
                  <MenuIcon name={definition.icon} size={14} />
                </span>
                <span className="truncate text-ui-md">{definition.title}</span>
              </label>

              {slot.visible && <SizeToggle slot={slot} onChange={(size) => setSize(slot.widgetKey, size)} />}

              <button
                type="button"
                aria-label={`${definition.title} 위로`}
                className="rounded p-1.5 disabled:opacity-30"
                style={{ color: 'var(--text-muted)' }}
                disabled={fixed || index === 0}
                onClick={() => move(slot.widgetKey, -1)}
              >
                <MenuIcon name="chevron-up" size={16} />
              </button>
              <button
                type="button"
                aria-label={`${definition.title} 아래로`}
                className="rounded p-1.5 disabled:opacity-30"
                style={{ color: 'var(--text-muted)' }}
                disabled={fixed || index === draft.length - 1}
                onClick={() => move(slot.widgetKey, 1)}
              >
                <MenuIcon name="chevron-down" size={16} />
              </button>
            </li>
          );
        })}
      </ul>
    </Modal>
  );
}

function SizeToggle({ slot, onChange }: { slot: WidgetSlot; onChange: (size: WidgetSize) => void }) {
  const definition = widgetByKey(slot.widgetKey);
  if (!definition || definition.sizes.length < 2) return null;

  const labels: Record<WidgetSize, string> = { sm: '보통', wide: '넓게' };

  return (
    <span className="flex shrink-0 gap-0.5">
      {definition.sizes.map((size) => (
        <button
          key={size}
          type="button"
          onClick={() => onChange(size)}
          className="rounded-md text-ui-2xs"
          style={{
            padding: '2px 7px',
            border: '1px solid var(--border-strong)',
            background: slot.size === size ? 'var(--accent-bg)' : 'var(--bg-card)',
            color: slot.size === size ? 'var(--accent)' : 'var(--text-muted)',
            fontWeight: slot.size === size ? 600 : 400,
          }}
          aria-pressed={slot.size === size}
        >
          {labels[size]}
        </button>
      ))}
    </span>
  );
}

/** 끌 수 없는가 — 코드가 정한 고정만이다(현장 머리). */
function pinned(widgetKey: string): boolean {
  return widgetByKey(widgetKey)?.fixed ?? false;
}
